import { FC, Fragment, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
// Hooks
import { ICaseData, IUserData, ResponseError } from 'app/hooks/useADCampaignSignup';
// MUI
import { Box, Typography, Button, Dialog, IconButton, useMediaQuery, Checkbox } from '@mui/material';
// Icons
import { Close as CloseIcon } from '@mui/icons-material';
// Components
import AccountStep from './AccountStep';
import ClaimantStep from './ClaimantStep';
import VerificationStep from './VerificationStep';
// MixPanel
import Mixpanel from 'app/services/Mixpanel.service';
import MixpanelTracks from 'app/types/MixpanelTracks';

interface IForm extends IUserData {
  rePassword: string;
  mfaCode: string;
  caseData: ICaseData;
}

type Props = {
  open: boolean;
  onClose: () => void;
  isLoading: boolean;
  error: ResponseError | null;
  handleSignup: (data: { caseData: ICaseData; userData: IUserData }, init?: RequestInit) => Promise<void>;
}

const SignupFormDialog:FC<Props> = ({
  open, onClose,
  isLoading, error, handleSignup
}) => {
  const [verificationSent, setVerificationSent] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState<string>('');
  const [agreed, setAgreed] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);

  const onChangeAgreed = (_:any, checked: boolean) => {
    setAgreed(checked)
  }

  const fullScreen = useMediaQuery('(max-width:600px)');

  const methods = useForm<IForm>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      password: '',
      rePassword: '',
      company: '',
      mfaCode: '',
      caseData: {
        claimant: {
          name: '',
          dateOfBirth: null
        },
        eventDate: null
      }
    }
  });

  const onSubmit = methods.handleSubmit(async (data: IForm) => {
    if (step === 1) {
      setStep(2);
      return;
    }

    const { caseData, mfaCode, rePassword, ...userData } = data;

    const init:RequestInit = {};
    if (mfaCode) {
      init['headers'] = {
        'MFA-Secret': mfaCode
      };
    }

    try {
      await handleSignup({ userData, caseData }, init)
        .then(() => {
          if (mfaCode) Mixpanel.track(MixpanelTracks.CampaignRegistrationCompleted);
        })
      if (mfaCode) onClose();
    } catch (error) {
      console.error(error);
    }
  });

  useEffect(() => {
    if (!error?.body) {
      setErrorText(null);
      return;
    }

    if (error.body.error === 'mfaRequired') {
      setVerificationSent(true);
      setErrorText(null);
      Mixpanel.track(MixpanelTracks.CampaignRegistrationVerificationStarted);
    } else {
      setErrorText(error.body.message || error.body.errors?.[0]?.message || '');
    }
  }, [error]);

  useEffect(() => {
    Mixpanel.track(MixpanelTracks.CampaignRegistrationStarted);
  }, []);

  const isDisabled = step === 1 && !agreed && !verificationSent;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      slotProps={{
        paper: {
          sx: {
            borderRadius: { xs: 0, sm: 4 },
            padding: '32px 40px',
            width: { xs: '100vw', sm: '100%' },
            maxWidth: { sm: 480 },
            margin: { xs: 0, sm: 'auto' },
            display: 'flex',
            flexDirection: 'column',
          },
        }
      }}
      sx={{ backdropFilter: 'blur(80px)' }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
      }}>
        {!verificationSent ? (
          <Box>
            <Typography sx={{
              fontWeight: 700,
              fontSize: { xs: 16, sm: 20 },
              lineHeight: '160%',
              letterSpacing: '0.15px',
              color: 'rgb(33, 33, 38)'
            }}>
              {step === 1 ? 'Try Case Chronology for free' : 'Claimant Information (Optional)'}
            </Typography>
            {step === 1 ? (
              <Typography sx={{
                  fontSize: { xs: 14, sm: 16 },
                  lineHeight: '150%',
                  letterSpacing: '0.15px',
                  color: 'rgb(33, 33, 38)'
              }}>Start a 7-day free trial.</Typography>
            ) : null}
          </Box>
        ) : <span />}
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <FormProvider {...methods}>
        <form noValidate onSubmit={onSubmit}>
          {verificationSent ? (
            <VerificationStep
              mfaCode={mfaCode}
              setMfaCode={setMfaCode}
            />
          ) : (
            <Fragment>
              {step === 1 && <AccountStep />}
              {step === 2 && <ClaimantStep />}
            </Fragment>
          )}

          {errorText ? (
            <Typography sx={{ color: 'error.main', fontSize: 13, textAlign: 'center', mt: 4 }}>{errorText}</Typography>
          ) : null}

          {step === 1 && !verificationSent ? (
            <Box component="label" sx={{
              mt: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Checkbox
                checked={agreed}
                onChange={onChangeAgreed}
              />
              <Typography sx={{
                fontSize: 14,
                'a': {
                  color: (theme) => theme.palette.primary.main,
                  textDecoration: 'none',
                }
              }}>
                I agree to the <a href="https://www.casechronology.com/terms-of-service" target="_blank" rel="noreferrer">Terms of Service</a> and <a href="https://www.casechronology.com/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>
              </Typography>
            </Box>
          ) : null}
          
          <Button
            fullWidth
            type="submit"
            variant="contained"
            loading={isLoading}
            size="large"
            sx={{ mt: 4 }}
            disabled={isDisabled}
          >
            {verificationSent ? 'Continue' : step === 1 ? 'Next' : 'Get Started'}
          </Button>
          {step === 2 && !verificationSent ? (
            <Button
              fullWidth
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => setStep(1)}
            >
              Back
            </Button>
          ) : null}
        </form>
      </FormProvider>
      <Typography sx={{
        mt: 4,
        fontSize: 14,
        textAlign: 'center',
        'a': {
          color: (theme) => theme.palette.primary.main,
          textDecoration: 'none',
        }
      }}>
        Questions? Give us a call. <a href="tel:+18133931262">+1 (813)393-1262</a>.
      </Typography>
    </Dialog>
  )
}

export default SignupFormDialog;
